/**
 * Active internet reachability probe.
 *
 * `navigator.onLine` only reports whether a network adapter is up. It stays `true` on Wi-Fi
 * without internet, router/ISP outages, VPN and virtual adapters (Hyper-V, Docker, VirtualBox),
 * so it cannot tell us that the network has recovered. This probe asks the backend instead.
 *
 * `mode: 'no-cors'` resolves (opaque) for any HTTP response and rejects only on a network
 * failure, so it needs no CORS configuration and any status code counts as "reachable".
 */

const PROBE_TIMEOUT_MS = 2500

function resolveProbeBaseUrl(): string {
  const base = (process.env.REACT_APP_BASE_URL || '').trim().replace(/\/+$/, '')
  if (base) return `${base}/health`
  return `${window.location.origin}/manifest.json`
}

export async function probeNetworkReachable(timeoutMs: number = PROBE_TIMEOUT_MS): Promise<boolean> {
  if (typeof fetch !== 'function') return true
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = setTimeout(() => controller?.abort(), timeoutMs)
  try {
    const url = `${resolveProbeBaseUrl()}?reachability=${Date.now()}`
    await fetch(url, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller?.signal,
    })
    return true
  } catch {
    return false
  } finally {
    clearTimeout(timer)
  }
}
